import { Component, inject, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ContactoInforme } from 'src/app/domain/model/informe/contacto-informe';
import { InformeCumplimientoRepository } from 'src/app/domain/service/informe/informe-cumplimiento-repository';
import {
  emailKeypress,
  isValidEmailLegacy,
  sanitizeEmailContacto,
  sanitizeTextPreserveSpaces
} from 'src/app/common/input-utils';

/** Filas fijas para adicionar contactos (legacy: 3 filas en GestionarContactosInformeViewImpl). */
const FILAS_NUEVOS_CONTACTOS = 3;

const MSG_ERROR_GENERICO =
  'Estimado usuario, en estos momentos estamos presentando fallas técnicas. Por favor contacte al administrador.';

/**
 * Diálogo "Gestionar Contactos del Informe".
 *
 * Legacy: GestionarContactosInformeActivity / GestionarContactosInformeViewImpl. Se abre tras enviar
 * un informe Final (EnviarInformeActivity) y tras registrar un cumplimiento con informe Final
 * (CreateOrUpdateCumplimientoActivity.gestionarContactosInforme).
 *
 * Flujo: ejecuta SP_SGS_GESTIONAR_CONTACS_INF (copia los contactos de los cumplimientos al informe)
 * y lista TSGS_INFORMES_CONTACTOS; permite editar, dar de baja y adicionar contactos.
 */
@Component({
  selector: 'app-gestionar-contactos-informe-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    MessageModule,
    ConfirmDialogModule
  ],
  providers: [ConfirmationService],
  template: `
    <!-- Modal Gestionar Contactos -->
    <p-dialog
      header="Gestionar Contactos del Informe"
      [(visible)]="dialogVisible"
      [modal]="true"
      [style]="{ width: '95vw', maxWidth: '52rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '92vw', '768px': '95vw', '640px': '98vw' }"
      [closable]="true"
      (onHide)="cerrarModalContactos()"
    >
      <div class="flex flex-col gap-4 px-1 sm:px-0">
        @if (avisoValidacion()) {
          <p-message
            severity="warn"
            text="Por favor valide y/o corrija las direcciones de correo electrónico de los contactos de la empresa que podrán visualizar el informe en Servicios en Línea"
            styleClass="w-full"
          />
        }

        <!-- Tabla de contactos existentes -->
        @if (contactosExistentes().length > 0) {
          <div class="md:hidden space-y-3">
            @for (
              contacto of contactosExistentes();
              track contacto.nmconsecutivo;
              let i = $index
            ) {
              <article
                class="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3"
              >
                @if (contactoEditandoIndex() === i) {
                  <div class="grid grid-cols-1 gap-2">
                    <input
                      pInputText
                      [(ngModel)]="contactoEditando.dsnombreContacto"
                      [maxlength]="maxNombreContacto"
                      (change)="contactoEditando.dsnombreContacto = sanitizeTextPreserveSpaces(contactoEditando.dsnombreContacto)"
                      class="w-full"
                      placeholder="Nombre"
                    />
                    <input
                      pInputText
                      [(ngModel)]="contactoEditando.dsmailContacto"
                      [maxlength]="maxEmailContacto"
                      (keypress)="emailKeypress($event)"
                      (change)="contactoEditando.dsmailContacto = sanitizeEmailContacto(contactoEditando.dsmailContacto)"
                      class="w-full"
                      type="email"
                      placeholder="Email"
                    />
                    <div class="flex justify-end gap-2">
                      <button
                        pButton
                        type="button"
                        icon="pi pi-check"
                        class="p-button-text p-button-success p-button-sm"
                        (click)="guardarEdicionContacto(i)"
                      ></button>
                      <button
                        pButton
                        type="button"
                        icon="pi pi-times"
                        class="p-button-text p-button-secondary p-button-sm"
                        (click)="cancelarEdicionContacto()"
                      ></button>
                    </div>
                  </div>
                } @else {
                  <div class="grid grid-cols-1 gap-1 text-sm">
                    <div>
                      <span class="text-gray-500 dark:text-gray-400">Nombre:</span>
                      <span class="text-gray-800 dark:text-gray-200 ml-1">{{ textoTabla(contacto.dsnombreContacto) }}</span>
                    </div>
                    <div>
                      <span class="text-gray-500 dark:text-gray-400">Email:</span>
                      <span class="text-gray-800 dark:text-gray-200 ml-1 break-all">{{ textoTabla(contacto.dsmailContacto) }}</span>
                    </div>
                  </div>
                  <div class="flex justify-end gap-2 pt-2">
                    <button
                      pButton
                      type="button"
                      icon="pi pi-pencil"
                      class="p-button-text p-button-sm"
                      (click)="editarContacto(i)"
                    ></button>
                    <button
                      pButton
                      type="button"
                      icon="pi pi-trash"
                      class="p-button-text p-button-danger p-button-sm"
                      (click)="confirmarEliminarContacto(contacto)"
                    ></button>
                  </div>
                }
              </article>
            }
          </div>

          <div class="hidden md:block overflow-x-auto">
            <table
              class="w-full text-sm border-collapse text-gray-700 dark:text-gray-200"
            >
              <thead>
                <tr
                  class="bg-gray-100 dark:bg-gray-700 text-left text-gray-700 dark:text-gray-200"
                >
                  <th class="p-2 border-b dark:border-gray-600">Nombre</th>
                  <th class="p-2 border-b dark:border-gray-600">Email</th>
                  <th
                    class="p-2 border-b dark:border-gray-600"
                    style="width: 120px"
                  >
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                @for (
                  contacto of contactosExistentes();
                  track contacto.nmconsecutivo;
                  let i = $index
                ) {
                  <tr
                    class="border-b dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    @if (contactoEditandoIndex() === i) {
                      <td class="p-2">
                        <input
                          pInputText
                          [(ngModel)]="contactoEditando.dsnombreContacto"
                          [maxlength]="maxNombreContacto"
                          (change)="contactoEditando.dsnombreContacto = sanitizeTextPreserveSpaces(contactoEditando.dsnombreContacto)"
                          class="w-full"
                        />
                      </td>
                      <td class="p-2">
                        <input
                          pInputText
                          [(ngModel)]="contactoEditando.dsmailContacto"
                          [maxlength]="maxEmailContacto"
                          (keypress)="emailKeypress($event)"
                          (change)="contactoEditando.dsmailContacto = sanitizeEmailContacto(contactoEditando.dsmailContacto)"
                          class="w-full"
                          type="email"
                        />
                      </td>
                      <td class="p-2">
                        <button
                          pButton
                          type="button"
                          icon="pi pi-check"
                          class="p-button-text p-button-success p-button-sm"
                          (click)="guardarEdicionContacto(i)"
                        ></button>
                        <button
                          pButton
                          type="button"
                          icon="pi pi-times"
                          class="p-button-text p-button-secondary p-button-sm"
                          (click)="cancelarEdicionContacto()"
                        ></button>
                      </td>
                    } @else {
                      <td class="p-2">{{ contacto.dsnombreContacto }}</td>
                      <td class="p-2">{{ contacto.dsmailContacto }}</td>
                      <td class="p-2">
                        <button
                          pButton
                          type="button"
                          icon="pi pi-pencil"
                          class="p-button-text p-button-sm"
                          (click)="editarContacto(i)"
                        ></button>
                        <button
                          pButton
                          type="button"
                          icon="pi pi-trash"
                          class="p-button-text p-button-danger p-button-sm"
                          (click)="confirmarEliminarContacto(contacto)"
                        ></button>
                      </td>
                    }
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }

        <!-- Adicionar nuevos contactos -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-semibold text-gray-700 dark:text-gray-300"
            >Adicionar contactos</label
          >
          @for (nuevo of nuevosContactos; track $index) {
            <div class="flex flex-col sm:flex-row gap-2 sm:items-center">
              <input
                pInputText
                [(ngModel)]="nuevo.dsnombreContacto"
                [maxlength]="maxNombreContacto"
                (change)="nuevo.dsnombreContacto = sanitizeTextPreserveSpaces(nuevo.dsnombreContacto)"
                placeholder="Nombre"
                class="flex-1"
              />
              <input
                pInputText
                [(ngModel)]="nuevo.dsmailContacto"
                [maxlength]="maxEmailContacto"
                (keypress)="emailKeypress($event)"
                (change)="nuevo.dsmailContacto = sanitizeEmailContacto(nuevo.dsmailContacto)"
                placeholder="Email"
                type="email"
                class="flex-1"
              />
              <button
                pButton
                icon="pi pi-trash"
                class="p-button-text p-button-danger p-button-sm self-end sm:self-auto"
                (click)="removerNuevoContacto($index)"

              ></button>
            </div>
          }
          @if (nuevosContactos.length < 3) {
            <button
              pButton
              type="button"
              label="Agregar fila"
              icon="pi pi-plus"
              class="p-button-text p-button-sm w-fit"
              (click)="agregarFilaNuevoContacto()"
            ></button>
          }
        </div>

        @if (contactosOkMsg()) {

          <p-message severity="success" [text]="contactosOkMsg()" styleClass="w-full" />

        }

        @if (contactosErrorMsg()) {
          <p-message
            severity="error"
            [text]="contactosErrorMsg()"
            styleClass="w-full"
          />
        }
      </div>

      <ng-template pTemplate="footer">
        <div class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full">
          <button
            pButton
            type="button"
            label="Cerrar"
            icon="pi pi-times"
            class="p-button-outlined p-button-secondary"
            (click)="cerrarModalContactos()"
          ></button>
          <button
            pButton
            type="button"
            label="Guardar"
            icon="pi pi-save"
            class="p-button-primary"
            [loading]="guardandoContactos()"
            (click)="guardarNuevosContactos()"
          ></button>
        </div>
      </ng-template>
    </p-dialog>

<p-confirmDialog key="contactosInformeConfirm" />
  `
})
export class GestionarContactosInformeDialogComponent {
  private readonly informeRepository = inject(InformeCumplimientoRepository);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  /** Se emite al cerrar el diálogo. */
  readonly cerrado = output<void>();

  // Legacy (GestionarContactosInformeViewImpl): Nombre máx. 80, Email máx. 100
  readonly maxNombreContacto = 80;
  readonly maxEmailContacto = 100;
  readonly sanitizeTextPreserveSpaces = sanitizeTextPreserveSpaces;
  readonly sanitizeEmailContacto = sanitizeEmailContacto;
  readonly emailKeypress = emailKeypress;

  dialogVisible = false;
  readonly contactosExistentes = signal<ContactoInforme[]>([]);
  readonly contactosErrorMsg = signal('');
  readonly contactosOkMsg = signal('');
  /** Legacy: el aviso de validación se muestra solo la primera vez que carga la lista. */
  readonly avisoValidacion = signal(false);
  readonly guardandoContactos = signal(false);
  /** Evita el doble clic en editar/eliminar mientras la petición está en vuelo (legacy: máscara de carga). */
  private operacionEnCurso = false;
  readonly contactoEditandoIndex = signal<number | null>(null);
  contactoEditando: ContactoInforme = { dsnombreContacto: '', dsmailContacto: '' };
  nuevosContactos: ContactoInforme[] = this.filasVacias();
  private nmconsecutivoInforme = 0;

  /**
   * Abre el diálogo para el informe indicado.
   *
   * @param nmconsecutivoInforme consecutivo (radicado) del informe
   * @param cumplimientos ids de cumplimiento separados por coma (ivacumplimientos del SP)
   */
  async abrir(nmconsecutivoInforme: number, cumplimientos: string): Promise<void> {
    this.nmconsecutivoInforme = nmconsecutivoInforme;
    this.reiniciarEstado();
    try {
      const contactos = await this.informeRepository.gestionarContactos(
        nmconsecutivoInforme,
        cumplimientos
      );
      this.contactosExistentes.set(contactos ?? []);
      // Legacy: si la consulta no trae datos, el ingreso de contactos no fue exitoso
      if (!contactos?.length) {
        this.contactosErrorMsg.set('No se pudieron ingresar los contactos');
      }
    } catch (err) {
      console.error('Error gestionando contactos', err);
      this.contactosErrorMsg.set(MSG_ERROR_GENERICO);
    }
    this.avisoValidacion.set(true);
    this.dialogVisible = true;
  }

  cerrarModalContactos(): void {
    this.dialogVisible = false;
    this.reiniciarEstado();
    this.contactosExistentes.set([]);
    this.cerrado.emit();
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  editarContacto(index: number): void {
    const contacto = this.contactosExistentes()[index];
    this.contactoEditando = { ...contacto };
    this.contactoEditandoIndex.set(index);
    this.limpiarMensajes();
  }

  cancelarEdicionContacto(): void {
    this.contactoEditandoIndex.set(null);
    this.limpiarMensajes();
  }

  /** Legacy: actualizarContactos; éxito → "Los datos se actualizaron correctamente." y recarga. */
  async guardarEdicionContacto(_index: number): Promise<void> {
    if (this.operacionEnCurso) return;
    this.limpiarMensajes();
    const faltantes: string[] = [];
    if (!this.contactoEditando.dsnombreContacto?.trim()) faltantes.push('Nombre');
    if (!this.contactoEditando.dsmailContacto?.trim()) faltantes.push('Email');
    if (faltantes.length) {
      this.contactosErrorMsg.set(
        `Por favor ingrese correctamente los siguientes campos: ${faltantes.join(', ')}`
      );
      return;
    }
    if (!isValidEmailLegacy(this.contactoEditando.dsmailContacto)) {
      this.contactosErrorMsg.set('El formato del E-mail es invalido');
      return;
    }
    this.operacionEnCurso = true;
    try {
      await this.informeRepository.actualizarContacto(this.nmconsecutivoInforme, {
        ...this.contactoEditando,
        dsnombreContacto: this.contactoEditando.dsnombreContacto.trim(),
        dsmailContacto: this.contactoEditando.dsmailContacto.trim()
      });
      this.contactoEditandoIndex.set(null);
      await this.recargar();
      this.contactosOkMsg.set('Los datos se actualizaron correctamente.');
    } catch (err) {
      console.error('Error actualizando contacto', err);
      this.contactosErrorMsg.set(MSG_ERROR_GENERICO);
    } finally {
      this.operacionEnCurso = false;
    }
  }

  /**
   * Legacy (SuraTable.addDeleteColumn): siempre pide confirmación antes de eliminar.
   */
  confirmarEliminarContacto(contacto: ContactoInforme): void {
    this.confirmationService.confirm({
      key: 'contactosInformeConfirm',
      header: 'Confirmación',
      message: '¿Esta seguro que desea eliminar el registro seleccionado?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí',
      rejectLabel: 'No',
      accept: () => void this.eliminarContacto(contacto)
    });
  }

  /** Legacy: baja lógica (febaja); éxito → "Los datos se eliminaron correctamente." y recarga. */
  async eliminarContacto(contacto: ContactoInforme): Promise<void> {
    if (this.operacionEnCurso) return;
    this.limpiarMensajes();
    if (typeof contacto.nmconsecutivo !== 'number') {
      this.contactosErrorMsg.set('No se pudo identificar el contacto a eliminar.');
      return;
    }
    this.operacionEnCurso = true;
    try {
      await this.informeRepository.eliminarContacto(
        this.nmconsecutivoInforme,
        contacto.nmconsecutivo
      );
      await this.recargar();
      this.contactosOkMsg.set('Los datos se eliminaron correctamente.');
    } catch (err) {
      console.error('Error eliminando contacto', err);
      this.contactosErrorMsg.set(MSG_ERROR_GENERICO);
    } finally {
      this.operacionEnCurso = false;
    }
  }

  /** Las 3 filas son fijas (legacy); "agregar fila" no aplica. */
  agregarFilaNuevoContacto(): void {
    // Sin efecto: el legacy muestra siempre 3 filas
  }

  /** Limpia la fila indicada (legacy: las filas son fijas). */
  removerNuevoContacto(index: number): void {
    this.nuevosContactos[index] = { dsnombreContacto: '', dsmailContacto: '' };
  }

  /**
   * Legacy (GestionarContactosInformeViewImpl / Activity.crearContactosInforme):
   * 1. Formato de cada email diligenciado: "El formato del E-mail adicional numero N es invalido".
   * 2. Completitud: "El contacto numero N debe tener la informacion completa( Nombre - Email )".
   * 3. Debe existir al menos un contacto.
   * Sin filas nuevas cierra; con filas, crea, cierra y muestra "La información se ha guardado con éxito".
   */
  async guardarNuevosContactos(): Promise<void> {
    this.limpiarMensajes();
    const lleno = (v: string | undefined | null) => !!v && v.trim().length > 0;

    for (let i = 0; i < this.nuevosContactos.length; i++) {
      const email = this.nuevosContactos[i].dsmailContacto;
      if (lleno(email) && !isValidEmailLegacy(email)) {
        this.contactosErrorMsg.set(`El formato del E-mail adicional numero ${i + 1} es invalido`);
        return;
      }
    }
    for (let i = 0; i < this.nuevosContactos.length; i++) {
      const c = this.nuevosContactos[i];
      if (lleno(c.dsnombreContacto) !== lleno(c.dsmailContacto)) {
        this.contactosErrorMsg.set(
          `El contacto numero ${i + 1} debe tener la informacion completa( Nombre - Email )`
        );
        return;
      }
    }

    const nuevos = this.nuevosContactos
      .filter((c) => lleno(c.dsnombreContacto))
      .map((c) => ({
        dsnombreContacto: c.dsnombreContacto.trim(),
        dsmailContacto: c.dsmailContacto.trim()
      }));

    if (nuevos.length === 0 && this.contactosExistentes().length === 0) {
      this.contactosErrorMsg.set('Debe existir al menos un contacto');
      return;
    }

    if (nuevos.length === 0) {
      this.cerrarConExito();
      return;
    }

    this.guardandoContactos.set(true);
    try {
      await this.informeRepository.crearContactos(this.nmconsecutivoInforme, nuevos);
      this.cerrarConExito();
    } catch (err) {
      console.error('Error creando contactos', err);
      // Legacy (NotificarInformeHandler.crearContactosInforme): por cada contacto que no se pudo crear,
      // "El Contacto : <nombre> No pudo ser creado [ERROR]"
      this.contactosErrorMsg.set(
        nuevos.map((c) => `El Contacto : ${c.dsnombreContacto} No pudo ser creado [ERROR]`).join('\n')
      );
    } finally {
      this.guardandoContactos.set(false);
    }
  }

  private cerrarConExito(): void {
    this.cerrarModalContactos();
    this.messageService.add({
      severity: 'success',
      summary: 'Éxito',
      detail: 'La información se ha guardado con éxito'
    });
  }

  private async recargar(): Promise<void> {
    const actualizados = await this.informeRepository.listarContactos(this.nmconsecutivoInforme);
    this.contactosExistentes.set(actualizados ?? []);
  }

  private limpiarMensajes(): void {
    this.contactosErrorMsg.set('');
    this.contactosOkMsg.set('');
    this.avisoValidacion.set(false);
  }

  private reiniciarEstado(): void {
    this.limpiarMensajes();
    this.contactoEditandoIndex.set(null);
    this.contactoEditando = { dsnombreContacto: '', dsmailContacto: '' };
    this.nuevosContactos = this.filasVacias();
  }

  private filasVacias(): ContactoInforme[] {
    return Array.from({ length: FILAS_NUEVOS_CONTACTOS }, () => ({
      dsnombreContacto: '',
      dsmailContacto: ''
    }));
  }
}
